import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Loader2, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useStore } from "../context/StoreContext";
import { resolveAsset } from "../hooks/useSiteContent";

const DEFAULT_COLORS = { bg: "#FDFDFD", text: "#0A121A", blue: "#0A1F3A", gold: "#D4AF37", surface: "#F4F5F7" };

function Section({ title, desc, children }) {
  return (
    <div className="bg-[color:var(--bg)] border border-ink/10 p-6 mb-6">
      <h3 className="font-display font-extrabold text-lg">{title}</h3>
      {desc && <p className="text-ink/50 text-sm mt-1 mb-5">{desc}</p>}
      <div className={desc ? "" : "mt-5"}>{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1.5 font-mono">{label}</label>
      {children}
    </div>
  );
}

const inp = "w-full border border-ink/15 bg-[color:var(--bg)] px-3.5 py-2.5 outline-none focus:border-gold transition-colors text-sm";

function ImageField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir la imagen"); } finally { setUploading(false); }
  };
  return (
    <div className="flex gap-4 items-start">
      <div className="w-24 h-24 bg-surface border border-ink/10 shrink-0 overflow-hidden flex items-center justify-center">
        {value ? <img src={resolveAsset(value)} alt="preview" className="w-full h-full object-contain" /> : <span className="text-ink/30 text-xs font-mono">sin imagen</span>}
      </div>
      <div className="flex-1 space-y-2">
        <div className="relative">
          <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Pega una URL o sube un archivo" data-testid={`${testid}-url`} className={`${inp} pl-9`} />
        </div>
        <label data-testid={`${testid}-upload`} className="inline-flex items-center gap-2 px-4 py-2 border border-ink/15 hover:border-gold hover:text-gold transition-colors cursor-pointer text-sm">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir archivo
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
    </div>
  );
}

export default function ContentPanel() {
  const { settings, refreshSettings, applyColors } = useStore();
  const [brand, setBrand] = useState(settings.brand_name);
  const [colors, setColors] = useState(settings.colors);
  const [content, setContent] = useState(settings.content || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBrand(settings.brand_name); setColors(settings.colors); setContent(settings.content || {}); }, [settings]);

  const setC = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const previewColor = (k, v) => { const next = { ...colors, [k]: v }; setColors(next); applyColors(next); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { brand_name: brand, colors, content });
      await refreshSettings();
      toast.success("Cambios guardados");
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  };
  const resetColors = () => { setColors(DEFAULT_COLORS); applyColors(DEFAULT_COLORS); };

  const swatches = [["blue", "Azul primario"], ["gold", "Dorado acento"], ["bg", "Fondo"], ["text", "Texto"], ["surface", "Superficie"]];

  return (
    <div className="max-w-2xl" data-testid="content-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Diseño & Contenido</h1>
          <p className="text-ink/50 text-sm mt-1">Edita marca, colores, textos, imágenes y tamaños. Los textos aplican al idioma español.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-content"
          className="px-6 py-3 bg-blue text-[color:var(--bg)] hover:bg-gold hover:text-inverse transition-colors font-medium disabled:opacity-60 whitespace-nowrap">
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      <Section title="Marca & Logo" desc="Nombre de la empresa, logotipo y su tamaño.">
        <Row label="Nombre de marca"><input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="brand-input" className={inp} /></Row>
        <Row label="Logotipo"><ImageField value={content.logo_url} onChange={(v) => setC("logo_url", v)} testid="logo" /></Row>
        <Row label={`Tamaño del logo: ${content.logo_size || 22}px`}>
          <input type="range" min="16" max="72" value={content.logo_size || 22} onChange={(e) => setC("logo_size", Number(e.target.value))} data-testid="logo-size" className="w-full accent-[color:var(--gold)]" />
        </Row>
      </Section>

      <Section title="Colores" desc="Se previsualizan al instante en todo el sitio.">
        {swatches.map(([k, lbl]) => (
          <div key={k} className="flex items-center justify-between border border-ink/10 p-3 mb-2">
            <span className="font-medium text-sm">{lbl}</span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-ink/50">{colors[k]}</span>
              <input type="color" value={colors[k]} onChange={(e) => previewColor(k, e.target.value)} data-testid={`color-${k}`} className="w-10 h-10 cursor-pointer border-0 bg-transparent" />
            </div>
          </div>
        ))}
        <button onClick={resetColors} className="mt-2 inline-flex items-center gap-2 text-sm text-ink/50 hover:text-gold transition-colors"><RotateCcw className="w-4 h-4" /> Restablecer colores</button>
      </Section>

      <Section title="Sección Héroe" desc="Imagen de fondo, textos y tamaño del título principal.">
        <Row label="Imagen de fondo"><ImageField value={content.hero_image} onChange={(v) => setC("hero_image", v)} testid="hero-image" /></Row>
        <Row label="Texto superior (overline)"><input value={content.hero_overline || ""} onChange={(e) => setC("hero_overline", e.target.value)} placeholder="Est. 1998 — Mercado global de minería" className={inp} /></Row>
        <div className="grid grid-cols-3 gap-3">
          <Row label="Título línea 1"><input value={content.hero_l1 || ""} onChange={(e) => setC("hero_l1", e.target.value)} data-testid="hero-l1" className={inp} /></Row>
          <Row label="Título línea 2"><input value={content.hero_l2 || ""} onChange={(e) => setC("hero_l2", e.target.value)} className={inp} /></Row>
          <Row label="Título línea 3"><input value={content.hero_l3 || ""} onChange={(e) => setC("hero_l3", e.target.value)} className={inp} /></Row>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Botón principal"><input value={content.hero_cta1 || ""} onChange={(e) => setC("hero_cta1", e.target.value)} placeholder="Explorar catálogo" className={inp} /></Row>
          <Row label="Botón secundario"><input value={content.hero_cta2 || ""} onChange={(e) => setC("hero_cta2", e.target.value)} placeholder="Servicios de consultoría" className={inp} /></Row>
        </div>
        <Row label={`Tamaño del título: ${content.hero_title_size || 100}%`}>
          <input type="range" min="60" max="140" value={content.hero_title_size || 100} onChange={(e) => setC("hero_title_size", Number(e.target.value))} data-testid="hero-title-size" className="w-full accent-[color:var(--gold)]" />
        </Row>
      </Section>

      <Section title="Banner de llamada a la acción" desc="Sección inferior de la página de inicio.">
        <Row label="Título"><input value={content.cta_title || ""} onChange={(e) => setC("cta_title", e.target.value)} placeholder="Diseñemos tu próxima operación minera." className={inp} /></Row>
        <Row label="Descripción"><textarea value={content.cta_desc || ""} onChange={(e) => setC("cta_desc", e.target.value)} rows={3} className={`${inp} resize-none`} /></Row>
      </Section>

      <Section title="Contacto (Footer)" desc="Datos de contacto en el pie de página.">
        <div className="grid grid-cols-2 gap-3">
          <Row label="Email"><input value={content.footer_email || ""} onChange={(e) => setC("footer_email", e.target.value)} placeholder="ventas@oomining.com" className={inp} /></Row>
          <Row label="Teléfono"><input value={content.footer_phone || ""} onChange={(e) => setC("footer_phone", e.target.value)} placeholder="+56 2 2345 6789" className={inp} /></Row>
        </div>
        <Row label="Ubicaciones (separadas por coma)"><input value={content.footer_locations || ""} onChange={(e) => setC("footer_locations", e.target.value)} placeholder="Santiago, Lima, Perth" className={inp} /></Row>
      </Section>

      <div className="sticky bottom-0 bg-surface py-4 -mx-6 md:-mx-10 px-6 md:px-10 border-t border-ink/10">
        <button onClick={save} disabled={saving} data-testid="save-content-bottom"
          className="px-6 py-3 bg-blue text-[color:var(--bg)] hover:bg-gold hover:text-inverse transition-colors font-medium disabled:opacity-60">
          {saving ? "Guardando…" : "Guardar todos los cambios"}
        </button>
      </div>
    </div>
  );
}
